/* eslint-disable react-perf/jsx-no-new-object-as-prop */
import Head from "next/head";

import { ProductPageContent, buildCategoryReferenceMap, dropProductBreadCrumbs } from "../../features";

import { useAlgoliaProductQuery } from "@byko/lib-algolia";

import { Breadcrumbs } from "@byko/component-breadcrumbs";
import { getProductPageBySlug } from "@byko/lib-api-dato-client";
import { restApi } from "@byko/lib-api-rest";
import { BykoProductPage } from "@byko/scene-byko-product-page";
import { ErrorScene } from "@byko/scene-error";

import type { NextPageContextExtra } from "../_app";
import type { BykoProductPage as BykoProductPageType } from "@byko/lib-api-dato-client";
import type { ProductDetail, VariantDetail } from "@byko/lib-api-rest";
import type { NextPageContext } from "next";
import type { AdditionalProducts, BreadCrumb } from "../../features";
import { useEffect, useMemo } from "react";
import { algoliaHitToProduct, useProductLowestPrices } from "@byko/lib-api-products";
import { ensureList, formatPriceNumber } from "@byko/lib-utils";
import { ProductRentCard, ProductStandardCard } from "@byko/component-product-card";
import { isRentalProduct } from "src/features/search/components/product-list";
import Script from "next/script";

interface SuccessProps {
  product: ProductDetail;
  cmsPage: BykoProductPageType | undefined;
  breadCrumbs: BreadCrumb[];
}

interface ErrorProps {
  errorCode: 404 | 500;
}

interface ProductAttributesLngType {
  en: string;
  "is-IS": string;
}

ProductPage.getInitialProps = async (
  ctx: NextPageContext & NextPageContextExtra,
): Promise<SuccessProps | ErrorProps> => {
  const slug = ctx.query["slug"] as string;

  let product;
  try {
    const { data } = await restApi.productsBySlugRetrieve(slug);
    product = data;
  } catch (e) {
    const error = e as { status: number };
    if (error && error.status === 404) {
      return { errorCode: 404 };
    }
    ctx.res && (ctx.res.statusCode = 500);
    return { errorCode: 500 };
  }

  if (!product) {
    ctx.res && (ctx.res.statusCode = 404);
    return { errorCode: 404 };
  }

  // FIXME: Shouldn't we at least report this error?
  const cmsPage = (await getProductPageBySlug()(slug).catch((e) => console.error(e))) ?? undefined;

  const catRefMap = buildCategoryReferenceMap(ctx.categories);
  const breadCrumbs = dropProductBreadCrumbs(catRefMap, product);

  return { product, cmsPage, breadCrumbs };
};

export default function ProductPage({
  //
  errorCode,
  product,
  cmsPage,
  breadCrumbs,
}: SuccessProps & ErrorProps) {
  const { data } = useAlgoliaProductQuery({ productIds: product?.idealAccessories ?? null });
  const pricedProducts = data?.hits
    ?.filter((p) => !(p.salesAvailability === "custom" || p.salesAvailability === "display" || isRentalProduct(p)))
    .map((p) => p.id);

  const { prices } = useProductLowestPrices(pricedProducts ?? []);
  const prods = ensureList(data?.hits.map(algoliaHitToProduct));

  const collection = useMemo((): JSX.Element[] => {
    const productCards = prods.map((relatedProduct, index) => {
      if (isRentalProduct(relatedProduct)) {
        return (
          <ProductRentCard
            key={relatedProduct.id}
            addedLabel="Leiguvara"
            listIndex={index}
            product={relatedProduct}
            smallMobileCard={true}
          />
        );
      } else {
        return (
          <ProductStandardCard
            key={relatedProduct.id}
            analyticsSectionId="related-product-slider"
            listIndex={index}
            lowestPriceSKU={
              prices?.[relatedProduct.id] !== undefined ? Object.keys(prices?.[relatedProduct.id] ?? {})[0] : undefined
            }
            prices={prices?.[relatedProduct.id]}
            product={relatedProduct}
            smallMobileCard={true}
          />
        );
      }
    });
    return productCards;
  }, [prices, prods]);

  let defaultVariant: VariantDetail | undefined;

  useEffect(() => {
    if (isRentalProduct(product)) {
      window.location.replace(`/leiguvara/${product.slug}`);
    }
  }, [product]);

  if (isRentalProduct(product)) {
    return null;
  }

  if (product == null) {
    return <ErrorScene code={404} />;
  }

  try {
    // prettier-ignore
    defaultVariant = (
      product?.defaultVariant == null
      ? product.variants[0]
      : product.variants.find((v) => product.defaultVariant === v.id)
      ) as VariantDetail;

    if (errorCode || defaultVariant == null) {
      return <ErrorScene code={errorCode} />;
    }
  } catch (e) {
    return <ErrorScene code={404} />;
  }

  const additionalProducts: AdditionalProducts = {
    productSliderSectionHeading: "Tengdar vörur",
    productSliderSectionCollection: collection,
    productSliderSectionParagraph: <></>,
  };

  const seoProductGroupSchema = {
    "@context": "https://schema.org",
    "@type": "ProductGroup",
    description: product.description?.is,
    name: product.name,
    productGroupID: product.attributes?.["groupId"] as string,
    category: (product.attributes?.["typeParent"] as ProductAttributesLngType[])?.map((t) => t["is-IS"]) ?? "",
    brand: (product.attributes?.["brand"] as string) ?? "",
    image: product.media?.map((m) => m.image?.productList) ?? "",
    hasEnergyConsumptionDetails: (product.attributes?.["energyTag"] as ProductAttributesLngType)?.["is-IS"] ?? "",
    hasCertification:
      (product.attributes?.["environmentalInfoType"] as ProductAttributesLngType[])?.[0]?.["is-IS"] ?? "",
    keywords: (product.attributes?.["seo"] as ProductAttributesLngType)?.["is-IS"] ?? "",
    hasVariant: product.variants.map((v) => ({
      "@type": "Product",
      name: v.name || product.name,
      sku: v.sku ?? "",
      color: v.meta?.color,
      image: (v.firstImage?.image?.productList || product.firstImage?.image?.productList) ?? "",
      description: product.description?.is,
      inProductGroupWithID: product.attributes?.["groupId"] as string,
      productID: `ean:${v.attributes?.["eanNumber"] as string}`,
      weight: v.attributes?.["weight"] as string,
      hasMeasurement: {
        "@type": "QuantitativeValue",
        unitText: v.meta?.unit,
        value: v.packagingQuantity ?? "",
      },
      size: v.meta?.size,
      offers: {
        "@type": "Offer",
        price: formatPriceNumber(v.price?.gross),
        priceCurrency: v.price?.currency,
        availability: v.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: `https://byko.is/vara/${product.slug}`,
      },
    })),
  };

  return (
    <>
      <Script id="product-metadata" type="application/ld+json">
        {JSON.stringify(seoProductGroupSchema)}
      </Script>
      <Head key="product-page">
        <title>BYKO - {product.name}</title>
      </Head>

      <Breadcrumbs breadcrumbs={breadCrumbs} />

      <ProductPageContent
        additionalProducts={collection.length ? additionalProducts : undefined}
        defaultVariant={defaultVariant}
        product={product}
        rental={false}
      />

      <BykoProductPage page={cmsPage} />
    </>
  );
}
